import { Injectable } from "@angular/core";
import { forkJoin, map, Observable, of, tap } from "rxjs";
import { ImportConfigurationClient, ImportConfigurationClientAccessType, ImportFieldAssignmentDefinitionsDTO, ImportFieldAssignmentTargetDefinitionDTO, ImportFunction, ImportFunctionDefinitionDTO, ImportType } from 'src/app/base/models/models.swagger.generated';

@Injectable({
  providedIn: 'root'
})
export class FieldAssignmentDefinitionsService  {
  private definitions: ImportFieldAssignmentDefinitionsDTO[];
  private observable: Observable<ImportFieldAssignmentDefinitionsDTO>[] = [];
  private clientAccessType: ImportConfigurationClientAccessType;

  constructor(private configurationClient: ImportConfigurationClient) {
  }

  public getTargetDefinitionsImmediately(importType: ImportType): ImportFieldAssignmentTargetDefinitionDTO[] {
    return this.definitions?.at(importType)?.targetDefinitions ?? [];
  }

  public getFunctionDefinitionsImmediately(importType: ImportType): ImportFunctionDefinitionDTO[] {
    return this.definitions?.at(importType)?.importFunctionDefinitions ?? [];
  }

  public loadTargetDefinitions(importType: ImportType, clientAccessType?: ImportConfigurationClientAccessType): Observable<ImportFieldAssignmentDefinitionsDTO[]> {
    if (this.definitions?.at(importType)) {
      return of(this.definitions.at(importType).targetDefinitions);
    }
    return this.loadAssignmentDefinitions(importType, clientAccessType).pipe(
      map((definitions: ImportFieldAssignmentDefinitionsDTO) => {
        return definitions.targetDefinitions;
      })
    );
  }

  public loadFunctionDefinitions(importType: ImportType, clientAccessType: ImportConfigurationClientAccessType): Observable<ImportFunctionDefinitionDTO[]> {
    if (this.clientAccessType === clientAccessType && this.definitions?.at(importType)) {
      return of(this.definitions.at(importType).importFunctionDefinitions);
    }
    return this.loadAssignmentDefinitions(importType, clientAccessType).pipe(
      map((definitions: ImportFieldAssignmentDefinitionsDTO) => {
        return definitions.importFunctionDefinitions;
      })
    );
  }

  private loadAssignmentDefinitions(importType: ImportType, clientAccessType: ImportConfigurationClientAccessType): Observable<ImportFieldAssignmentDefinitionsDTO> {
    if (this.clientAccessType === clientAccessType && this.observable?.at(importType)) {
      return this.observable[importType];
    }
    this.clientAccessType = clientAccessType;
    this.observable[importType] = this.configurationClient.getFieldAssignmentDefinitions(importType, clientAccessType).pipe(
      tap({next: (definitions: ImportFieldAssignmentDefinitionsDTO) => {
        if (!this.definitions) {
          this.definitions = [];
        }
        
        this.definitions[importType] = definitions;
        this.observable[importType] = null;
      }, error: () => {
        this.observable[importType] = null;
      }}));
    return this.observable[importType];
  }

  public loadAllFiledAssignmentDefinitions(clientAccessType: ImportConfigurationClientAccessType): Observable<ImportFieldAssignmentDefinitionsDTO[]> {
    if (this.clientAccessType === clientAccessType && this.definitions) {
      return of(this.definitions);
    }
    // get all keys of ImportType
    const keys = Object.keys(ImportType).filter(k => typeof ImportType[k as any] === "number");
    const observables = keys.map(key => this.loadAssignmentDefinitions(ImportType[key as keyof typeof ImportType], clientAccessType));
    return forkJoin(observables);
  }

  public isStaticFunction(importFunction: ImportFunction): boolean {
    return importFunction === ImportFunction.ToToday
    || importFunction === ImportFunction.FixDate
    || importFunction === ImportFunction.NoData
    || importFunction === ImportFunction.VendorNoLookup;
  }
}
